import React, { useState, useRef } from 'react';
import { Maximize2, X } from 'lucide-react';

interface VideoPlayerProps {
  url: string;
  title: string;
}

export default function VideoPlayer({ url, title }: VideoPlayerProps) {
  const [showModal, setShowModal] = useState(false);
  const smallVideoRef = useRef<HTMLVideoElement>(null);
  const modalVideoRef = useRef<HTMLVideoElement>(null);

  const handleVideoClick = (e: React.MouseEvent<HTMLVideoElement>) => {
    // Get click coordinates relative to the video element
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if click is on the controls area (bottom 40px of video)
    const isControlsClick = y > (rect.height - 40);

    if (!isControlsClick) {
      e.preventDefault();
      if (smallVideoRef.current) {
        smallVideoRef.current.pause();
      }
      setShowModal(true);
    }
  };

  const handleOpenModal = () => {
    if (smallVideoRef.current) {
      smallVideoRef.current.pause();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (modalVideoRef.current) {
      modalVideoRef.current.pause();
    }
    setShowModal(false);
  };

  return (
    <>
      <div className="relative aspect-video w-full group">
        <video
          ref={smallVideoRef}
          src={url}
          className="w-full h-full object-cover cursor-pointer"
          onClick={handleVideoClick}
          controls
        />
        <button
          onClick={handleOpenModal}
          className="absolute top-2 right-2 p-2 bg-black/50 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity"
          title="Open in fullscreen"
        >
          <Maximize2 className="w-5 h-5" />
        </button>
      </div>

      {/* Fullscreen Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
          <div className="relative w-full h-full flex flex-col items-center justify-center p-4">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 p-2 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <h2 className="text-white text-xl font-semibold mb-4">{title}</h2>
            
            <div className="relative w-full max-w-6xl">
              <video
                ref={modalVideoRef}
                src={url}
                className="w-full"
                controls
                autoPlay
                controlsList="nodownload"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}
    </>
  );
}